export class APIGlue {
  constructor(devtools) {
    this.devtools = devtools;
    this.plugins = new Map();
    this.hooks = new Map();
  }

  init() {
    this.exposeAPI();
  }

  exposeAPI() {
    // Expose global API for extending devtools
    window.DevToolsAPI = {
      // Window management
      createWindow: (config) => this.createCustomWindow(config),
      closeWindow: (id) => this.devtools.windowManager.closeWindow(id),
      showErrorTable: () => this.devtools.windowManager.createErrorTableWindow(),
      showSettings: () => this.devtools.settingsWindow.createSettingsWindow(),
      
      // Theme management
      setTheme: (themeId) => this.devtools.themeManager.applyTheme(themeId),
      setLayout: (layoutId) => this.devtools.themeManager.applyLayout(layoutId),
      getThemes: () => this.devtools.themeManager.getAvailableThemes(),
      getLayouts: () => this.devtools.themeManager.getAvailableLayouts(),
      
      // Service integration
      connectService: (name, config) => this.devtools.serviceManager.connectService(name, config),
      sendToService: (service, data) => this.devtools.serviceManager.sendErrorToService(service, data),
      
      // Plugin system
      registerPlugin: (name, plugin) => this.registerPlugin(name, plugin),
      unregisterPlugin: (name) => this.unregisterPlugin(name),
      
      // Hooks system
      addHook: (event, callback) => this.addHook(event, callback),
      removeHook: (event, callback) => this.removeHook(event, callback),
      triggerHook: (event, data) => this.triggerHook(event, data),
      
      // Data access
      getErrors: () => this.devtools.runtimeManager.errors,
      getOperations: () => this.devtools.runtimeManager.operations,
      
      // Utilities
      log: (message, data) => this.customLog(message, data),
      captureSnapshot: () => this.captureSnapshot()
    };
  }

  createCustomWindow(config) {
    const windowId = `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const defaultConfig = {
      title: 'Custom Window',
      content: '',
      width: 400,
      height: 300,
      closable: true,
      draggable: true
    };

    const finalConfig = { ...defaultConfig, ...config };
    
    const windowElement = document.createElement('div');
    windowElement.className = 'devtools-window';
    windowElement.style.width = finalConfig.width + 'px';
    windowElement.style.height = finalConfig.height + 'px';
    windowElement.style.zIndex = ++this.devtools.windowManager.zIndexCounter;
    
    windowElement.innerHTML = `
      <div class="devtools-header">
        <div class="devtools-title">${finalConfig.title}</div>
        <div class="devtools-controls">
          ${finalConfig.closable ? '<button class="devtools-btn close-btn">✕</button>' : ''}
        </div>
      </div>
      <div class="devtools-content" style="max-height: ${finalConfig.height - 40}px;">
        ${finalConfig.content}
      </div>
    `;

    document.body.appendChild(windowElement);
    
    if (finalConfig.closable) {
      const closeBtn = windowElement.querySelector('.close-btn');
      closeBtn.addEventListener('click', () => {
        windowElement.remove();
        this.devtools.windowManager.windows.delete(windowId);
      });
    }

    if (finalConfig.draggable) {
      this.devtools.windowManager.makeWindowDraggable(windowElement);
    }

    this.devtools.windowManager.positionWindow(windowElement);
    this.devtools.windowManager.windows.set(windowId, {
      element: windowElement,
      data: finalConfig,
      type: 'custom'
    });

    return windowId;
  }

  registerPlugin(name, plugin) {
    if (typeof plugin.init === 'function') {
      plugin.init(this.devtools);
    }
    
    this.plugins.set(name, plugin);
    this.triggerHook('plugin:registered', { name, plugin });
  }

  unregisterPlugin(name) {
    const plugin = this.plugins.get(name);
    if (plugin && typeof plugin.destroy === 'function') {
      plugin.destroy();
    }
    
    this.plugins.delete(name);
    this.triggerHook('plugin:unregistered', { name });
  }

  addHook(event, callback) {
    if (!this.hooks.has(event)) {
      this.hooks.set(event, new Set());
    }
    this.hooks.get(event).add(callback);
  }

  removeHook(event, callback) {
    if (this.hooks.has(event)) {
      this.hooks.get(event).delete(callback);
    }
  }

  triggerHook(event, data) {
    if (this.hooks.has(event)) {
      this.hooks.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Hook error for event "${event}":`, error);
        }
      });
    }
  }

  customLog(message, data) {
    const logData = {
      message,
      data,
      timestamp: Date.now(),
      type: 'custom'
    };

    this.devtools.runtimeManager.logOperation({
      type: 'custom-log',
      data: logData
    });

    console.log(`[DevTools] ${message}`, data);
  }

  captureSnapshot() {
    return {
      timestamp: Date.now(),
      errors: [...this.devtools.runtimeManager.errors],
      operations: [...this.devtools.runtimeManager.operations],
      windows: Array.from(this.devtools.windowManager.windows.keys()),
      plugins: Array.from(this.plugins.keys())
    };
  }
}